import {
  Component,
  OnInit,
  Input,
  Output,
  EventEmitter,
  ChangeDetectionStrategy,
} from '@angular/core';
import { Vector2, Vector3 } from 'three';
import { BehaviorSubject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { Plane2Model, PlaneTextureData } from '../../model';
import { EntityDragEvent } from '../mouse/mouse-entity.service';
import { BoundingBox2FromCenterSize } from '../../share/boundingbox2';
import {
  ScaleDirection,
  CornerDirection,
  Ng3EdgeResizeEvent,
  Ng3CornerResizeEvent,
} from './resize-helper';

@Component({
  selector: 'ng3-resize-plane',
  templateUrl: './resize-plane.component.html',
  styleUrls: ['./resize-plane.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ResizePlaneComponent implements OnInit {
  readonly edgeDirections: ScaleDirection[] = [
    'left',
    'right',
    'top',
    'bottom',
  ];
  readonly cornerDirections: CornerDirection[] = [
    { x: 'left', y: 'top' },
    { x: 'right', y: 'top' },
    { x: 'left', y: 'bottom' },
    { x: 'right', y: 'bottom' },
  ];
  private modelSubject = new BehaviorSubject<Plane2Model>(DEFAULT_MAIN_MODEL);
  readonly model$ = this.modelSubject.asObservable();
  readonly models$ = this.model$.pipe(
    map(m => {
      return {
        main: m,
        bars: this.createBars(m),
        corners: this.createCorners(m),
      };
    }),
  );
  public initialied = false;

  @Input()
  get model() {
    return this.modelSubject.value;
  }
  set model(m: Plane2Model) {
    this.modelSubject.next(m);
  }

  @Input() z = 1;
  @Input() mainTexture: PlaneTextureData = DEFAULT_MAIN_TEXTURE;
  @Input() barTexture: PlaneTextureData = DEFAULT_BAR_TEXTURE;
  @Output() resizeEdge = new EventEmitter<Ng3EdgeResizeEvent>();
  @Output() resizeCorner = new EventEmitter<Ng3CornerResizeEvent>();

  constructor() {}

  ngOnInit() {}
  private createBars(model: Plane2Model) {
    const result: { [d: string]: Plane2Model } = {};
    this.edgeDirections.forEach(d => {
      result[d] = barModel(model, d, this.barTexture);
    });
    return result;
  }
  private createCorners(model: Plane2Model) {
    return this.cornerDirections.map(direction => {
      return {
        direction,
        model: cornerModel(model, direction, DEFAULT_CORNER_TEXTURE),
      };
    });
  }
  onDragBar(e: EntityDragEvent, direction: ScaleDirection) {
    this.resizeEdge.emit({
      type: 'edge',
      direction,
      to:
        direction === 'left' || direction === 'right'
          ? e.relativeCenter.x
          : e.relativeCenter.y,
    });
  }
  onResizeEdge(e: Ng3EdgeResizeEvent) {
    this.resizeEdge.emit(e);
  }
  onResizeCorner(e: Ng3CornerResizeEvent) {
    this.resizeCorner.emit(e);
  }
  onDragCorner(e: EntityDragEvent, direction: CornerDirection) {
    this.resizeCorner.emit({
      type: 'corner',
      direction,
      to: e.relativeCenter,
    });
  }
  modelOf(
    direction: CornerDirection,
    models: { direction: CornerDirection; model: Plane2Model }[],
  ) {
    const result = models.find(m => m.direction === direction);
    if (result === undefined) {
      throw Error(
        `Can not find corner model of direction: ${direction.x} ${direction.y}`,
      );
    }
    return result.model;
  }
}

function offset(direction: ScaleDirection, xOrY: 'x' | 'y', main: Plane2Model) {
  switch (direction) {
    case 'left':
      return xOrY === 'x' ? -main.box.size.x / 2 : 0;
    case 'right':
      return xOrY === 'x' ? main.box.size.x / 2 : 0;
    case 'top':
      return xOrY === 'y' ? -main.box.size.y / 2 : 0;
    case 'bottom':
      return xOrY === 'y' ? main.box.size.y / 2 : 0;
  }
}

function size(
  direction: ScaleDirection,
  m: Plane2Model,
  position: 'edge' | 'corner',
) {
  const ratio = 0.2;
  if (position === 'edge') {
    switch (direction) {
      case 'left':
      case 'right':
        return new Vector2(m.box.size.x * ratio, m.box.size.y);
      case 'top':
      case 'bottom':
        return new Vector2(m.box.size.x, m.box.size.y * ratio);
    }
  }
  return new Vector2(m.box.size.x * ratio, m.box.size.y * ratio);
}

function barModel(
  m: Plane2Model,
  direction: ScaleDirection,
  texture: PlaneTextureData,
) {
  const o = (xOrY: 'x' | 'y') => offset(direction, xOrY, m);

  return new Plane2Model(
    new BoundingBox2FromCenterSize(
      new Vector2(m.box.center.x + o('x'), m.box.center.y + o('y')),
      size(direction, m, 'edge'),
    ),
    10,
    texture,
    new Vector3(0, 0, 0),
    true,
    1,
  );
}

function cornerModel(
  m: Plane2Model,
  direction: CornerDirection,
  texture: PlaneTextureData,
) {
  return new Plane2Model(
    new BoundingBox2FromCenterSize(
      new Vector2(
        m.box.center.x + offset(direction.x, 'x', m),
        m.box.center.y + offset(direction.y, 'y', m),
      ),
      size(direction.x, m, 'corner'),
    ),
    10,
    texture,
    new Vector3(0, 0, 0),
    true,
    0.8,
  );
}
const DEFAULT_MAIN_MODEL = new Plane2Model(
  new BoundingBox2FromCenterSize(new Vector2(0, 0), new Vector2(10, 10)),
  10,
  {
    r: new Uint8Array([255]),
    g: new Uint8Array([255]),
    b: new Uint8Array([255]),
    shape: new Vector2(1, 1),
  },
);

const DEFAULT_MAIN_TEXTURE: PlaneTextureData = {
  r: new Uint8Array([255]),
  g: new Uint8Array([255]),
  b: new Uint8Array([255]),
  shape: new Vector2(1, 1),
};
const DEFAULT_BAR_TEXTURE: PlaneTextureData = {
  r: new Uint8Array([128]),
  g: new Uint8Array([128]),
  b: new Uint8Array([128]),
  shape: new Vector2(1, 1),
};
const DEFAULT_CORNER_TEXTURE: PlaneTextureData = {
  r: new Uint8Array([64]),
  g: new Uint8Array([64]),
  b: new Uint8Array([64]),
  shape: new Vector2(1, 1),
};
